import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/shared/SEO";
import { useI18n } from "../i18n/useI18n";

type Founder = {
  name: string;
  role: string;
  bio: string;
};

const founders: Record<"en" | "id", Founder[]> = {
  en: [
    {
      name: "Aril Haromi",
      role: "Chief Executive Officer (CEO)",
      bio: "Experienced across multiple Indonesian unicorns. Strong in people management, analytics, and strategic mediation to keep teams aligned and focused.",
    },
    {
      name: "Rana Loda Tama",
      role: "Chief Technology Officer (CTO)",
      bio: "The technical force behind execution. Continuously tracks the latest technology, builds practical AI systems, and turns ambitious ideas into production realities.",
    },
    {
      name: "Dimas Kurniawan",
      role: "Chief Information Officer (CIO)",
      bio: "Leads long-term strategy and information direction with deep industry experience, including strong legal-tech expertise built across complex real-world implementations.",
    },
  ],
  id: [
    {
      name: "Aril Haromi",
      role: "Chief Executive Officer (CEO)",
      bio: "Berpengalaman bekerja di beberapa unicorn di Indonesia. Kuat di people management, analisa, dan menjadi penengah yang efektif untuk menjaga tim tetap satu arah.",
    },
    {
      name: "Rana Loda Tama",
      role: "Chief Technology Officer (CTO)",
      bio: "Otak teknis di balik eksekusi. Selalu mengikuti perkembangan teknologi terbaru, membangun solusi AI yang nyata, dan membuat hal-hal yang terlihat sulit jadi mungkin.",
    },
    {
      name: "Dimas Kurniawan",
      role: "Chief Information Officer (CIO)",
      bio: "Berfokus pada strategi jangka panjang dan arah informasi perusahaan, dengan pengalaman panjang di industri termasuk pengalaman mendalam di ranah legal-tech.",
    },
  ],
};

const projectHighlights: Record<"en" | "id", string[]> = {
  en: [
    "Enterprise identity and secure access initiatives for high-scale environments",
    "AI-assisted customer and operations workflows, including deployments.",
    "Hospitality chatbot analytics and service-quality insight dashboards.",
    "Public-sector and geospatial data analysis projects for decision support.",
    "Procurement intelligence, vendor analytics, and executive reporting workflows.",
  ],
  id: [
    "Inisiatif enterprise identity dan secure access untuk lingkungan berskala besar",
    "Workflow AI untuk customer service dan operasi, termasuk implementasi.",
    "Analitik chatbot hospitality dan dashboard insight kualitas layanan.",
    "Proyek analisis data sektor publik dan geospasial untuk dukungan pengambilan keputusan.",
    "Procurement intelligence, analitik vendor, dan workflow reporting untuk pimpinan.",
  ],
};

export default function CompanyPage() {
  const { lang } = useI18n();
  const foundersData = founders[lang];
  const highlights = projectHighlights[lang];

  const title = lang === "id" ? "Tentang Perusahaan" : "About Company";
  const description =
    lang === "id"
      ? "Profil PT Rekayasa Analisa Digital, tim pendiri, dan rekam jejak proyek."
      : "PT Rekayasa Analisa Digital profile, founding team, and project track record.";

  return (
    <div className="bg-slate-50">
      <SEO
        title={`${title} | ERICA by Rekayasa`}
        description={description}
        lang={lang}
      />

      <div className="mx-auto max-w-6xl px-4 pb-20 pt-28 sm:px-6 lg:px-8">
        <section className="rounded-3xl border border-slate-200 bg-white px-6 py-10 shadow-sm sm:px-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              {title}
            </h1>
            <Link
              to="/"
              className="text-sm font-semibold text-slate-700 hover:text-slate-900"
            >
              {lang === "id" ? "Kembali ke ERICA" : "Back to ERICA"}
            </Link>
          </div>

          <p className="mt-6 max-w-4xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {lang === "id"
              ? "PT Rekayasa Analisa Digital resmi berdiri pada 2023, tetapi perjalanan tim dimulai jauh sebelumnya. Sejak sekitar 2005, kami sudah membangun, mengimplementasikan, dan mengembangkan berbagai solusi digital untuk banyak kebutuhan bisnis dan institusi."
              : "PT Rekayasa Analisa Digital was officially established in 2023, but the team journey began much earlier. Since around 2005, we have been building and delivering digital solutions across a wide range of business and institutional needs."}
          </p>
          <p className="mt-4 max-w-4xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {lang === "id"
              ? "Hari ini, ERICA menjadi fokus utama kami. Namun DNA Rekayasa tetap sama: kombinasi eksekusi engineering yang kuat, pragmatisme bisnis, dan visi jangka panjang."
              : "Today, ERICA is our core focus. The Rekayasa DNA remains the same: strong engineering execution, business pragmatism, and long-term vision."}
          </p>
        </section>

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white px-6 py-10 shadow-sm sm:px-10">
          <h2 className="text-2xl font-semibold text-slate-900">
            {lang === "id" ? "Tim Pendiri" : "Founding Team"}
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {foundersData.map((founder) => (
              <article
                key={founder.name}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-sm font-semibold text-white">
                  {founder.name
                    .split(" ")
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  {founder.name}
                </h3>
                <p className="mt-1 text-sm font-medium text-slate-600">
                  {founder.role}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  {founder.bio}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white px-6 py-10 shadow-sm sm:px-10">
          <h2 className="text-2xl font-semibold text-slate-900">
            {lang === "id" ? "Rekam Jejak Proyek" : "Project Track Record"}
          </h2>
          <p className="mt-4 max-w-4xl text-base leading-relaxed text-slate-600">
            {lang === "id"
              ? "Sebelum landing page berfokus penuh ke ERICA, kami telah mengerjakan banyak proyek lintas domain. Jejak ini masih terlihat pada codebase lama dan menjadi fondasi kemampuan produk kami saat ini."
              : "Before this landing page focused fully on ERICA, we delivered many cross-domain projects. That history is still reflected in the previous codebase and remains a core foundation of our current product capabilities."}
          </p>
          <ul className="mt-6 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
            {highlights.map((item) => (
              <li
                key={item}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
              >
                {item}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
